// MyTcItemsPage — "My TC items": the transaction-coordinator checklist steps given to me
// across every deal, soonest first, with overdue / due-soon flags; tick them done or n/a
// here. "Everything in my scope" lists every open step on the deals I may reach (an agent:
// their own deals; a team lead: the team's; the managing broker and office: all) — the
// server narrows it with lib/recordAccess. API: /api/tc/mine
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';

const FLAG = {
    overdue: 'bg-red-100 text-red-800', at_risk: 'bg-amber-100 text-amber-800', open: 'bg-slate-100 text-slate-700',
    no_date: 'bg-slate-100 text-slate-500', done: 'bg-emerald-100 text-emerald-800', na: 'bg-slate-100 text-slate-400',
};
const FLAG_LABEL = { overdue: 'overdue', at_risk: 'due soon', open: 'open', no_date: 'no date', done: 'done', na: 'n/a' };
const ROLE_LABELS = {
    agent: 'Our agent', tc: 'Office staff / TC', managing_broker: 'Managing broker', attorney: 'Attorney',
    lender: 'Lender', title: 'Title / closing agent', inspector: 'Inspector', client: 'Client',
};
const usDate = (ymd) => (ymd ? `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}/${ymd.slice(0, 4)}` : '—');

export default function MyTcItemsPage() {
    const api = useApiClient();
    const user = getCurrentUser();
    const readOnly = user?.role === 'examiner';
    const [all, setAll] = useState(false);
    const [includeDone, setIncludeDone] = useState(false);
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        try {
            const q = new URLSearchParams();
            if (all) q.set('all', '1');
            if (includeDone) q.set('include_done', '1');
            setData(await api.get(`/api/tc/mine?${q}`)); setErr('');
        } catch (e) { setErr(e.message); }
    }, [api, all, includeDone]);
    useEffect(() => { load(); }, [load]);

    const setStatus = async (it, status) => {
        try { await api.put(`/api/tc/transactions/${it.transaction_id}/items/${it.item_id}`, { status }); load(); } catch (e) { setErr(e.message); }
    };

    const s = data?.summary || { open: 0, overdue: 0, at_risk: 0 };
    return (
        <div className="p-6 space-y-4">
            <div className="flex flex-wrap justify-between gap-3 items-start">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">My TC items</h1>
                    <p className="text-sm text-slate-500">Transaction coordinator checklist steps across your deals. Open a deal to edit a step, change who holds it or type a date.</p>
                </div>
                <div className="flex gap-2 text-sm">
                    <span className={`px-2 py-1 rounded ${FLAG.overdue}`}>{s.overdue} overdue</span>
                    <span className={`px-2 py-1 rounded ${FLAG.at_risk}`}>{s.at_risk} due soon</span>
                    <span className="px-2 py-1 rounded bg-slate-100">{s.open} open</span>
                </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-1"><input type="radio" checked={!all} onChange={() => setAll(false)} />given to me</label>
                <label className="flex items-center gap-1"><input type="radio" checked={all} onChange={() => setAll(true)} />everything in my scope</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />include done / n/a</label>
            </div>
            {err && <p className="text-red-700 text-sm">{err}</p>}
            <div className="bg-white p-6 rounded-lg shadow-md">
                {!data ? 'Loading…' : data.items.length === 0 ? <p className="text-slate-500">Nothing here. Steps appear once a deal's TC checklist is created on the deal page.</p> : (
                    <DataList rows={data.items} rowKey="item_id" csvName="tc-items" defaultSort={{ key: 'due_date', dir: 'asc' }}
                        filters={[{ key: 'flag', label: 'Flag' }, { key: 'role', label: 'Who' }, { key: 'deal', label: 'Deal' }]}
                        rowClass={(it) => (it.status !== 'open' ? 'text-slate-400' : '')}
                        columns={[
                            { key: 'due_date', label: 'Due', type: 'date', render: (it) => <span className="whitespace-nowrap">{usDate(it.due_date)}</span> },
                            { key: 'label', label: 'Step', value: (it) => `${it.label}${it.note ? ` (note: ${it.note})` : ''}`, sortValue: (it) => it.label,
                                render: (it) => <>{it.label}{it.note && <span className="block text-xs text-slate-500">Note: {it.note}</span>}</> },
                            { key: 'deal', label: 'Deal', value: (it) => (it.street_address ? `${it.street_address}${it.city ? `, ${it.city}` : ''}` : `Deal #${it.transaction_id}`),
                                render: (it) => <Link to={`/dashboard/transactions/${it.transaction_id}`} className="text-indigo-700">{it.street_address ? `${it.street_address}${it.city ? `, ${it.city}` : ''}` : `Deal #${it.transaction_id}`}</Link> },
                            { key: 'role', label: 'Who', value: (it) => ROLE_LABELS[it.role] || it.role },
                            { key: 'assigned_name', label: 'Assigned to', render: (it) => it.assigned_name || '' },
                            { key: 'flag', label: 'Flag', value: (it) => FLAG_LABEL[it.flag] || it.flag, render: (it) => <span className={`px-2 py-0.5 rounded ${FLAG[it.flag]}`}>{FLAG_LABEL[it.flag]}</span> },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (it) => (
                                <span className="whitespace-nowrap space-x-2">
                                    {!readOnly && (it.status === 'open'
                                        ? <><button onClick={() => setStatus(it, 'done')} className="text-emerald-700">Done</button><button onClick={() => setStatus(it, 'na')} className="text-slate-500">N/A</button></>
                                        : <button onClick={() => setStatus(it, 'open')} className="text-slate-500">Reopen</button>)}
                                </span>) },
                        ]} />
                )}
            </div>
        </div>
    );
}
